/**
 * Forms domain API client. Wraps the shared `apiFetch` helper for the authed
 * builder + responses flows and the public (unauthenticated) render/submit flow.
 */
import type {
  CreateFormInput,
  Form,
  FormDetail,
  FormSubmission,
  Paginated,
  PublicForm,
  PublicSubmitInput,
  PublicSubmitResult,
  UpdateFormInput,
} from '@fc/core'
import { apiFetch } from './api'

export type {
  CreateFormInput,
  Form,
  FormDetail,
  FormField,
  FormFieldInput,
  FormStatus,
  FormSubmission,
  PublicForm,
  UpdateFormInput,
} from '@fc/core'

// ---- Authed builder ----
export const listForms = async (): Promise<Form[]> => {
  const data = await apiFetch<Paginated<Form>>('/forms')
  return data.items
}

export const getForm = (id: string): Promise<FormDetail> => apiFetch<FormDetail>(`/forms/${id}`)

export const createForm = (input: CreateFormInput): Promise<FormDetail> =>
  apiFetch<FormDetail>('/forms', { method: 'POST', body: JSON.stringify(input) })

export const updateForm = (id: string, input: UpdateFormInput): Promise<FormDetail> =>
  apiFetch<FormDetail>(`/forms/${id}`, { method: 'PATCH', body: JSON.stringify(input) })

export const deleteForm = (id: string): Promise<void> =>
  apiFetch<void>(`/forms/${id}`, { method: 'DELETE' })

// ---- Authed responses ----
export const listResponses = async (
  formId: string,
  page = 1,
  pageSize = 50,
): Promise<Paginated<FormSubmission>> =>
  apiFetch<Paginated<FormSubmission>>(
    `/forms/${formId}/responses?page=${page}&pageSize=${pageSize}`,
  )

// ---- Public (unauthenticated) ----
export const getPublicForm = (orgSlug: string, formSlug: string): Promise<PublicForm> =>
  apiFetch<PublicForm>(
    `/forms/public/${encodeURIComponent(orgSlug)}/${encodeURIComponent(formSlug)}`,
  )

export const submitPublicForm = (
  orgSlug: string,
  formSlug: string,
  input: PublicSubmitInput,
): Promise<PublicSubmitResult> =>
  apiFetch<PublicSubmitResult>(
    `/forms/public/${encodeURIComponent(orgSlug)}/${encodeURIComponent(formSlug)}/submit`,
    { method: 'POST', body: JSON.stringify(input) },
  )
